import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, AlertTriangle, CheckCircle2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, get, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SkeletonRows, InfoTip } from "@/components/app/Common";
import { TestSend } from "@/pages/settings/EmailTestSend";

function StatusBanner({ configured }) {
  return configured ? (
    <div className="flex items-center gap-2 rounded-[6px] bg-success-tint px-4 py-3 text-sm text-success" data-testid="email-status-banner"><CheckCircle2 size={16} /> Live mode: email is configured and messages are sent through Resend.</div>
  ) : (
    <div className="flex flex-wrap items-center gap-2 rounded-[6px] border border-[#E8D7B0] bg-caution-tint px-4 py-3 text-sm text-caution" data-testid="email-status-banner">
      <AlertTriangle size={16} /> <strong>Test mode:</strong> emails are saved to the outbox, not sent.
      <Link to="/app/settings/outbox" className="ml-auto inline-flex items-center gap-1 font-medium underline" data-testid="email-banner-outbox-link"><Inbox size={14} /> Open outbox</Link>
    </div>
  );
}

const Step = ({ n, title, hint, done, children }) => (
  <li className="grid gap-3 sm:grid-cols-[32px_1fr]">
    <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${done ? "bg-success-tint text-success" : "bg-brand-tint text-brand"}`}>{done ? <CheckCircle2 size={16} /> : n}</span>
    <div className="space-y-2"><p className="font-medium text-ink">{title}</p><p className="text-xs text-ink-secondary">{hint}</p>{children}</div>
  </li>
);

export default function EmailSettings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings-email"], queryFn: () => get("/settings/email") });
  const [key, setKey] = useState("");
  const [from, setFrom] = useState(null);
  const [name, setName] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async (body, msg) => {
    setBusy(true);
    try {
      await api.put("/settings/email", body);
      setKey("");
      qc.invalidateQueries({ queryKey: ["settings-email"] });
      toast.success(msg);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (isLoading) return <SkeletonRows rows={4} />;
  const fromValue = from ?? data.from_address ?? "";
  const nameValue = name ?? data.from_name ?? "";
  return (
    <section className="panel space-y-5 p-6" data-testid="email-settings">
      <div><h2 className="text-lg font-semibold">Email</h2><p className="text-sm text-ink-secondary">Candidate emails (invites, outcomes, data requests) are sent from your own domain through Resend.</p></div>
      <StatusBanner configured={data.configured} />
      <ol className="space-y-6" data-testid="email-setup-steps">
        <Step n={1} title="Choose who emails come from" hint="Use a real inbox candidates can reply to. The domain must be verified in Resend (Domains → Add domain)." done={!!data.from_address}>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5"><Label htmlFor="from-name">Sender name</Label>
              <Input id="from-name" value={nameValue} onChange={(e) => setName(e.target.value)} placeholder="Your law firm hiring team" data-testid="email-from-name-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="from-address">Sender address</Label>
              <Input id="from-address" type="email" value={fromValue} onChange={(e) => setFrom(e.target.value)} placeholder="hiring@yourdomain.com" data-testid="email-from-input" /></div>
          </div>
        </Step>
        <Step n={2} title="Add your Resend API key" hint="Create one at resend.com → API Keys with “Sending access”. It's stored encrypted on the server and never shown in full again." done={data.configured}>
          <div className="max-w-md space-y-1.5">
            <Label htmlFor="resend-key" className="flex items-center gap-1.5">Resend API key <InfoTip text="Stored securely on the server. It is never shown in full or sent back to the browser." /></Label>
            <Input id="resend-key" type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder={data.api_key_masked || "re_…"} data-testid="email-api-key-input" />
            {data.api_key_masked && <p className="text-xs text-ink-muted" data-testid="email-api-key-masked">Current key: {data.api_key_masked}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => save({ resend_api_key: key || null, from_address: fromValue, from_name: nameValue || null }, "Email settings saved")} data-testid="email-save-btn">
              {busy && <Loader2 size={16} className="animate-spin" />} Save email settings
            </Button>
            {data.configured && <Button variant="ghost" className="text-risk" disabled={busy} onClick={() => save({ clear_api_key: true }, "API key removed")} data-testid="email-clear-key-btn">Remove key</Button>}
          </div>
        </Step>
        <Step n={3} title="Send a test email" hint={data.configured ? "Check it arrives, and isn't in spam." : "In test mode this lands in the outbox so you can preview it."}>
          <TestSend defaultTo={user.email} />
        </Step>
      </ol>
      <p className="text-sm text-ink-secondary">Every email, sent or not, is listed in the <Link to="/app/settings/outbox" className="text-brand hover:underline" data-testid="email-outbox-link">email outbox</Link>.</p>
    </section>
  );
}
